import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Download, FileSpreadsheet, Radar, Upload } from 'lucide-react';
import { PageHead } from '../components/page-head.js';
import { useT } from '../lib/i18n.js';
import { errorMessage } from '../lib/properties.js';
import {
  fileToBase64,
  importFromSreality,
  importTable,
  IMPORT_MAPPING_FIELDS,
  isWaitingForAccess,
  MAX_IMPORT_FILE_BYTES,
  previewImportTable,
  vendorSampleUrl,
  type ImportColumnMapping,
  type ImportMappingField,
  type ImportTablePreview,
  type ImportTableResult,
  type SrealityMigrationResult,
} from '../lib/office-migration-api.js';
import {
  importRadarListings,
  previewRadarListings,
  type RadarMigrationPreview,
} from '../lib/office-migration-radar-api.js';

/**
 * Krok QAFIX2 — OBRAZOVKA „Prevod kancelare" (`/import`).
 *
 * Nalez N03 QA-DEN-20261020: krok IMPORT1 dodal pravidla importu a routy, ale
 * v adminu nebyla ZADNA cesta, jak se k nim dostat (`/import` = Not Found).
 * Tahle obrazovka je ta cesta a drzi se poradi, ve kterem o tom makler
 * uvazuje: odkud data mam -> co kam patri -> jak to bude vypadat -> potvrdit.
 *
 * TRI ZDROJE, JEDNO POTVRZENI: tabulka z jejich softwaru (funguje hned),
 * ucet Sreality a verejne inzeraty z akvizicniho radaru. Vsechny tri konci
 * KONCEPTY zakazek — na portaly se neodesila nic (to je vlastnost serveru,
 * obrazovka to jen rika nahlas, aby se toho makler nebal).
 *
 * Chybejici pristupy NEJSOU chyba obrazovky: cesty Sreality i radaru maji
 * sedy stav „Hotovo, ceka na pristupy" (vzor GCAL1/PORT1).
 */

type TableStep = 'upload' | 'mapping' | 'done';

/** Pole mapovani -> klic textu; jeden seznam, aby formular a napoveda sedely. */
const MAPPING_LABEL_KEYS: Readonly<Record<ImportMappingField, string>> = {
  externalId: 'webAdmin.officeMigration.fieldExternalId',
  title: 'webAdmin.officeMigration.fieldTitle',
  description: 'webAdmin.officeMigration.fieldDescription',
  addressText: 'webAdmin.officeMigration.fieldAddressText',
  kind: 'webAdmin.officeMigration.fieldKind',
  priceAmount: 'webAdmin.officeMigration.fieldPriceAmount',
  photos: 'webAdmin.officeMigration.fieldPhotos',
};

function cardStyle(): React.CSSProperties {
  return { padding: 16, marginBottom: 16 };
}

/** Cena z faktu nabidky („50820.0000" -> „50 820 Kč"); chybi -> pomlcka. */
function previewPrice(facts: readonly { readonly factKey: string; readonly value: unknown }[]): string {
  const fact = facts.find((item) => item.factKey === 'asking_price');
  const value = fact?.value;
  if (value === null || typeof value !== 'object') return '—';
  const amount = (value as { amount?: unknown }).amount;
  if (typeof amount !== 'string') return '—';
  const number = Number(amount);
  if (!Number.isFinite(number)) return '—';
  return `${number.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč`;
}

export function ImportPage(): React.JSX.Element {
  const t = useT();

  return (
    <div className="p-6" data-testid="import-page">
      <PageHead
        title={t('webAdmin.officeMigration.title')}
        subtitle={t('webAdmin.officeMigration.perex')}
      />
      <div style={{ maxWidth: 860 }}>
        <TableImportCard />
        <SamplesCard />
        <SrealityCard />
        <RadarCard />
      </div>
    </div>
  );
}

/** Cesta 1 — tabulka XLSX/CSV. Nahrani -> mapovani -> nahled -> souhrn. */
function TableImportCard(): React.JSX.Element {
  const t = useT();
  const [step, setStep] = React.useState<TableStep>('upload');
  const [file, setFile] = React.useState<{ readonly name: string; readonly base64: string } | null>(null);
  const [preview, setPreview] = React.useState<ImportTablePreview | null>(null);
  const [mapping, setMapping] = React.useState<ImportColumnMapping | null>(null);
  const [result, setResult] = React.useState<ImportTableResult | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function pick(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (picked === undefined) return;
    setError(null);
    if (picked.size > MAX_IMPORT_FILE_BYTES) {
      setError(t('webAdmin.officeMigration.fileTooLarge'));
      return;
    }
    if (!/\.(xlsx|csv)$/i.test(picked.name)) {
      setError(t('webAdmin.officeMigration.fileUnsupported'));
      return;
    }
    setBusy(true);
    try {
      const base64 = await fileToBase64(picked);
      const loaded = await previewImportTable({ fileName: picked.name, contentBase64: base64 });
      setFile({ name: picked.name, base64 });
      setPreview(loaded);
      setMapping(loaded.mapping ?? loaded.suggestion.mapping);
      setStep('mapping');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  /** Rucni premapovani sloupce — nahled se prepocita na serveru. */
  async function changeColumn(field: ImportMappingField, column: string): Promise<void> {
    if (file === null) return;
    const next: ImportColumnMapping = {
      externalId: field === 'externalId' ? column : (mapping?.externalId ?? ''),
      title: field === 'title' ? column : (mapping?.title ?? ''),
      ...buildOptional(mapping, field, column),
    };
    setMapping(next);
    if (next.externalId === '' || next.title === '') return;
    setBusy(true);
    setError(null);
    try {
      setPreview(
        await previewImportTable({ fileName: file.name, contentBase64: file.base64, mapping: next }),
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    if (file === null || mapping === null) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await importTable({ fileName: file.name, contentBase64: file.base64, mapping }));
      setStep('done');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function reset(): void {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setMapping(null);
    setResult(null);
    setError(null);
  }

  const missingRequired = mapping === null || mapping.externalId === '' || mapping.title === '';

  return (
    <div className="rh-card" style={cardStyle()} data-testid="import-table-card">
      <h2 className="mb-1 text-base font-semibold">
        <FileSpreadsheet size={16} aria-hidden="true" /> {t('webAdmin.officeMigration.tableTitle')}
      </h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.officeMigration.tablePerex')}</p>

      {error !== null ? (
        <p role="alert" className="mb-2 text-sm text-red-600" data-testid="import-error">
          {error}
        </p>
      ) : null}

      {step === 'upload' ? (
        <label className="rh-btn rh-btn--primary" style={{ display: 'inline-flex', gap: 8 }}>
          <Upload size={16} aria-hidden="true" />
          {t('webAdmin.officeMigration.filePick')}
          <input
            type="file"
            accept=".xlsx,.csv"
            onChange={(event) => void pick(event)}
            data-testid="import-file-input"
            style={{ display: 'none' }}
          />
        </label>
      ) : null}

      {step === 'mapping' && preview !== null ? (
        <div data-testid="import-mapping">
          <p className="mb-2 text-sm" data-testid="import-file-name">
            {t('webAdmin.officeMigration.fileSelected', { name: file?.name ?? '' })}
          </p>
          <h3 className="mb-1 text-sm font-semibold">{t('webAdmin.officeMigration.mappingTitle')}</h3>
          <p className="mb-2 text-sm text-gray-600">{t('webAdmin.officeMigration.mappingPerex')}</p>
          <div style={{ display: 'grid', gap: 8 }}>
            {IMPORT_MAPPING_FIELDS.map((field) => (
              <label key={field} className="text-sm" style={{ display: 'grid', gap: 4 }}>
                {t(MAPPING_LABEL_KEYS[field] as Parameters<typeof t>[0])}
                <select
                  className="rh-input"
                  data-testid={`import-mapping-${field}`}
                  value={mapping?.[field] ?? ''}
                  onChange={(event) => void changeColumn(field, event.target.value)}
                >
                  <option value="">{t('webAdmin.officeMigration.mappingNone')}</option>
                  {preview.header.map((column) => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {preview.suggestion.unmapped.length > 0 ? (
            <p className="mt-2 text-sm text-gray-600" data-testid="import-unmapped">
              {t('webAdmin.officeMigration.mappingUnmapped', {
                columns: preview.suggestion.unmapped.join(', '),
              })}
            </p>
          ) : null}

          <h3 className="mb-1 mt-4 text-sm font-semibold">{t('webAdmin.officeMigration.previewTitle')}</h3>
          <p className="mb-2 text-sm text-gray-600" data-testid="import-preview-summary">
            {t('webAdmin.officeMigration.previewSummary', {
              rows: preview.totalRows,
              listings: preview.totalListings,
            })}
          </p>
          {/* `.rh-table` je OBAL tabulky (kit.css styluje `.rh-table table`). */}
          <div className="rh-table" style={{ overflowX: 'auto' }}>
            <table className="text-sm" data-testid="import-preview-table">
              <thead>
                <tr>
                  <th>{t('webAdmin.officeMigration.previewColumnId')}</th>
                  <th>{t('webAdmin.officeMigration.previewColumnTitle')}</th>
                  <th>{t('webAdmin.officeMigration.previewColumnKind')}</th>
                  <th>{t('webAdmin.officeMigration.previewColumnAddress')}</th>
                  <th>{t('webAdmin.officeMigration.previewColumnPrice')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, index) => (
                  <tr key={row.externalId} data-testid={`import-preview-row-${String(index)}`}>
                    <td>{row.externalId}</td>
                    <td>{row.title}</td>
                    <td>{row.kind}</td>
                    <td>{row.addressText ?? '—'}</td>
                    <td>{previewPrice(row.facts)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.errors.length > 0 ? (
            <details className="mt-2 text-sm" data-testid="import-row-errors">
              <summary>
                {t('webAdmin.officeMigration.previewRowErrors', { count: preview.errors.length })}
              </summary>
              <ul>
                {preview.errors.map((rowError) => (
                  <li key={rowError}>{rowError}</li>
                ))}
              </ul>
            </details>
          ) : null}

          {missingRequired ? (
            <p role="alert" className="mt-2 text-sm text-red-600" data-testid="import-mapping-missing">
              {t('webAdmin.officeMigration.mappingMissingRequired')}
            </p>
          ) : null}
          <p className="mt-3 text-sm text-gray-600">{t('webAdmin.officeMigration.confirmHint')}</p>
          <div className="mt-2" style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="rh-btn rh-btn--primary"
              disabled={busy || missingRequired}
              onClick={() => void confirm()}
              data-testid="import-confirm"
            >
              {busy ? t('webAdmin.officeMigration.running') : t('webAdmin.officeMigration.confirm')}
            </button>
            <button type="button" className="rh-btn" onClick={reset} data-testid="import-back">
              {t('webAdmin.officeMigration.back')}
            </button>
          </div>
        </div>
      ) : null}

      {step === 'done' && result !== null ? (
        <div data-testid="import-summary">
          <h3 className="mb-1 text-sm font-semibold">{t('webAdmin.officeMigration.summaryTitle')}</h3>
          <ul className="text-sm">
            <li data-testid="import-summary-created">
              {t('webAdmin.officeMigration.summaryCreated', { count: result.createdCases })}
            </li>
            <li data-testid="import-summary-updated">
              {t('webAdmin.officeMigration.summaryUpdated', { count: result.updatedCases })}
            </li>
            <li data-testid="import-summary-skipped">
              {t('webAdmin.officeMigration.summarySkipped', { count: result.skippedRows })}
            </li>
            <li data-testid="import-summary-errors">
              {t('webAdmin.officeMigration.summaryErrors', { count: result.errors.length })}
            </li>
          </ul>
          {result.errors.length > 0 ? (
            <button
              type="button"
              className="rh-btn mt-2"
              onClick={() => downloadErrorRows(result.errors)}
              data-testid="import-errors-download"
            >
              <Download size={16} aria-hidden="true" /> {t('webAdmin.officeMigration.summaryErrorsDownload')}
            </button>
          ) : null}
          <div className="mt-3" style={{ display: 'flex', gap: 8 }}>
            <Link to="/nemovitosti" className="rh-btn rh-btn--primary" data-testid="import-summary-properties">
              {t('webAdmin.officeMigration.summaryToProperties')}
            </Link>
            <button type="button" className="rh-btn" onClick={reset} data-testid="import-summary-again">
              {t('webAdmin.officeMigration.summaryAgain')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Nepovinna pole mapovani. `exactOptionalPropertyTypes` nedovoluje poslat
 * `undefined`, takze se klic bud prida, nebo tam neni vubec.
 */
function buildOptional(
  current: ImportColumnMapping | null,
  field: ImportMappingField,
  column: string,
): Partial<ImportColumnMapping> {
  const optional: Partial<Record<ImportMappingField, string>> = {
    ...(current?.description !== undefined ? { description: current.description } : {}),
    ...(current?.addressText !== undefined ? { addressText: current.addressText } : {}),
    ...(current?.kind !== undefined ? { kind: current.kind } : {}),
    ...(current?.priceAmount !== undefined ? { priceAmount: current.priceAmount } : {}),
    ...(current?.photos !== undefined ? { photos: current.photos } : {}),
  };
  if (field !== 'externalId' && field !== 'title') {
    if (column === '') delete optional[field];
    else optional[field] = column;
  }
  return optional;
}

/** Chybove radky ke stazeni — jeden radek CSV na chybu, bez knihovny. */
function downloadErrorRows(errors: readonly string[]): void {
  const csv = `chyba\n${errors.map((row) => `"${row.replace(/"/g, '""')}"`).join('\n')}\n`;
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'prevod-kancelare-chyby.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/** Bod 4 zadani — vzory XLSX pro Softreal a Realman. */
function SamplesCard(): React.JSX.Element {
  const t = useT();
  return (
    <div className="rh-card" style={cardStyle()} data-testid="import-samples-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.officeMigration.samplesTitle')}</h2>
      <p className="mb-2 text-sm text-gray-600">{t('webAdmin.officeMigration.samplesPerex')}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <a className="rh-btn" href={vendorSampleUrl('softreal')} data-testid="import-sample-softreal">
          <Download size={16} aria-hidden="true" /> {t('webAdmin.officeMigration.sampleSoftreal')}
        </a>
        <a className="rh-btn" href={vendorSampleUrl('realman')} data-testid="import-sample-realman">
          <Download size={16} aria-hidden="true" /> {t('webAdmin.officeMigration.sampleRealman')}
        </a>
      </div>
    </div>
  );
}

/** Sedy stav „ceka na pristupy" — vzor GCAL1/PORT1, ne chyba obrazovky. */
function WaitingForAccess({
  testId,
  titleKey,
  perexKey,
  withPortalsLink,
}: {
  readonly testId: string;
  readonly titleKey: string;
  readonly perexKey: string;
  readonly withPortalsLink: boolean;
}): React.JSX.Element {
  const t = useT();
  return (
    <div className="mt-2 text-sm" role="status" data-testid={testId}>
      <p className="font-semibold">{t(titleKey as Parameters<typeof t>[0])}</p>
      <p className="text-gray-600">{t(perexKey as Parameters<typeof t>[0])}</p>
      {withPortalsLink ? (
        <Link to="/portaly" className="underline" data-testid={`${testId}-link`}>
          {t('webAdmin.officeMigration.waitingLink')}
        </Link>
      ) : null}
    </div>
  );
}

/** Cesta 2 — ucet Sreality (jen cteni z portalu). */
function SrealityCard(): React.JSX.Element {
  const t = useT();
  const [confirming, setConfirming] = React.useState(false);
  const [waiting, setWaiting] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<SrealityMigrationResult | null>(null);

  async function run(): Promise<void> {
    setBusy(true);
    setError(null);
    setWaiting(false);
    try {
      setResult(await importFromSreality());
      setConfirming(false);
    } catch (caught) {
      if (isWaitingForAccess(caught)) setWaiting(true);
      else setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-card" style={cardStyle()} data-testid="import-sreality-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.officeMigration.srealityTitle')}</h2>
      <p className="mb-2 text-sm text-gray-600">{t('webAdmin.officeMigration.srealityPerex')}</p>

      {error !== null ? (
        <p role="alert" className="mb-2 text-sm text-red-600" data-testid="import-sreality-error">
          {error}
        </p>
      ) : null}

      {result === null ? (
        <>
          <button
            type="button"
            className="rh-btn"
            disabled={busy}
            onClick={() => {
              setConfirming(true);
              void run();
            }}
            data-testid="import-sreality-start"
          >
            {busy ? t('webAdmin.officeMigration.loading') : t('webAdmin.officeMigration.srealityStart')}
          </button>
          {confirming && !waiting && !busy && error === null ? (
            <div className="mt-2 text-sm" data-testid="import-sreality-confirm-panel">
              <p className="font-semibold">{t('webAdmin.officeMigration.srealityConfirmTitle')}</p>
              <p className="text-gray-600">{t('webAdmin.officeMigration.srealityConfirmPerex')}</p>
            </div>
          ) : null}
        </>
      ) : (
        <div className="text-sm" data-testid="import-sreality-summary">
          <p>{t('webAdmin.officeMigration.summaryCreated', { count: result.createdCases })}</p>
          <p>{t('webAdmin.officeMigration.summaryUpdated', { count: result.updatedCases })}</p>
          {result.notes.length > 0 ? (
            <details>
              <summary>{t('webAdmin.officeMigration.srealityNotes')}</summary>
              <ul>
                {result.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}

      {waiting ? (
        <WaitingForAccess
          testId="import-sreality-waiting"
          titleKey="webAdmin.officeMigration.waitingTitle"
          perexKey="webAdmin.officeMigration.waitingPerex"
          withPortalsLink
        />
      ) : null}
    </div>
  );
}

/** Cesta 3 — verejne inzeraty kancelare z akvizicniho radaru. */
function RadarCard(): React.JSX.Element {
  const t = useT();
  const [agency, setAgency] = React.useState('');
  const [preview, setPreview] = React.useState<RadarMigrationPreview | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [imported, setImported] = React.useState<number | null>(null);

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    setImported(null);
    try {
      setPreview(await previewRadarListings(agency));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function run(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await importRadarListings(agency);
      setImported(result.createdCases);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const waiting = preview !== null && !preview.available;

  return (
    <div className="rh-card" style={cardStyle()} data-testid="import-radar-card">
      <h2 className="mb-1 text-base font-semibold">
        <Radar size={16} aria-hidden="true" /> {t('webAdmin.officeMigration.radarTitle')}
      </h2>
      <p className="mb-2 text-sm text-gray-600">{t('webAdmin.officeMigration.radarPerex')}</p>

      {error !== null ? (
        <p role="alert" className="mb-2 text-sm text-red-600" data-testid="import-radar-error">
          {error}
        </p>
      ) : null}

      <label className="text-sm" style={{ display: 'grid', gap: 4, maxWidth: 420 }}>
        {t('webAdmin.officeMigration.radarAgencyLabel')}
        <input
          className="rh-input"
          value={agency}
          onChange={(event) => setAgency(event.target.value)}
          data-testid="import-radar-agency"
        />
      </label>
      <button
        type="button"
        className="rh-btn mt-2"
        disabled={busy || agency.trim().length < 3}
        onClick={() => void load()}
        data-testid="import-radar-load"
      >
        {busy ? t('webAdmin.officeMigration.loading') : t('webAdmin.officeMigration.radarLoad')}
      </button>

      {waiting ? (
        <WaitingForAccess
          testId="import-radar-waiting"
          titleKey="webAdmin.officeMigration.radarWaitingTitle"
          perexKey="webAdmin.officeMigration.radarWaitingPerex"
          withPortalsLink={false}
        />
      ) : null}

      {preview !== null && preview.available ? (
        <div className="mt-2 text-sm" data-testid="import-radar-preview">
          {preview.items.length === 0 ? (
            <p data-testid="import-radar-empty">{t('webAdmin.officeMigration.radarEmpty')}</p>
          ) : (
            <>
              <p data-testid="import-radar-found">
                {t('webAdmin.officeMigration.radarFound', { count: preview.items.length })}
              </p>
              <ul>
                {preview.items.slice(0, 20).map((item) => (
                  <li key={item.externalId}>
                    {item.title}
                    {item.addressText === null ? '' : ` — ${item.addressText}`}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600">{t('webAdmin.officeMigration.confirmHint')}</p>
              <button
                type="button"
                className="rh-btn rh-btn--primary mt-2"
                disabled={busy}
                onClick={() => void run()}
                data-testid="import-radar-import"
              >
                {busy ? t('webAdmin.officeMigration.running') : t('webAdmin.officeMigration.radarImport')}
              </button>
            </>
          )}
        </div>
      ) : null}

      {imported !== null ? (
        <p className="mt-2 text-sm" data-testid="import-radar-summary">
          {t('webAdmin.officeMigration.summaryCreated', { count: imported })}
        </p>
      ) : null}
    </div>
  );
}
